import { useCallback, useEffect, useRef } from 'react';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { createMarkers } from './utils/markers.utils';
import {
  createSeriesMarkers,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  SeriesType,
  Time
} from 'lightweight-charts';
import { toNumericTimeOrNull } from './utils/time-normalization.utils';

function snapToPreviousCandleTime(
  entryTime: Time,
  candlesticks: { time: Time }[]
) {
  const entryTs = toNumericTimeOrNull(entryTime);
  if (entryTs == null) return undefined;

  // Find the latest candlestick <= entryTime
  let snapped = candlesticks[0]?.time;
  for (const c of candlesticks) {
    const cTs = toNumericTimeOrNull(c.time);
    if (cTs != null && cTs <= entryTs) {
      snapped = c.time;
    } else {
      break;
    }
  }
  return snapped;
}

export const useMarkers = () => {
  const {
    candlestickSeriesRef,
    chartInitialized,
    data: {
      analysis: { signals },
      execution: { entries, adds }
    }
  } = useChartContext();
  const { candlestickData$, formState, timeframeIndex } = useChartStreamContext();
  const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const markersSeriesRef = useRef<ISeriesApi<SeriesType, Time> | null>(null);

  const clearMarkers = useCallback(() => {
    if (!markersPluginRef.current) return;
    // Explicit clear before detach/update to ensure old marker primitives are removed.
    markersPluginRef.current.setMarkers([]);
  }, []);

  const detachMarkers = useCallback(() => {
    if (!markersPluginRef.current) return;
    markersPluginRef.current.setMarkers([]);
    markersPluginRef.current.detach();
    markersPluginRef.current = null;
    markersSeriesRef.current = null;
  }, []);

  const ensureMarkersPlugin = useCallback(() => {
    const series = candlestickSeriesRef.current;
    if (!series) return null;

    // If series instance changed (recreate/scope change), detach old plugin first.
    if (markersSeriesRef.current && markersSeriesRef.current !== series) {
      detachMarkers();
    }

    if (!markersPluginRef.current) {
      markersPluginRef.current = createSeriesMarkers(series, []);
      markersSeriesRef.current = series;
    }

    return markersPluginRef.current;
  }, [candlestickSeriesRef, detachMarkers]);

  const renderMarkers = useCallback(
    (candlesticks: { time: Time }[]) => {
      if (!chartInitialized || !candlestickSeriesRef.current) return;
      const markersPlugin = ensureMarkersPlugin();

      if (!markersPlugin) return;

      const signalMarkers =
        signals.raw?.flatMap(({ type, side, data }, signalIndex) =>
          createMarkers(data, side, 'orange', {
            idPrefix: `signal-${type}-${side}-${signalIndex}`
          })
        ) || [];

      const longEntryTriggerMarkers = createMarkers(
        signals.triggers.long,
        'LONG',
        'pink'
      );

      const shortEntryTriggersMarkers = createMarkers(
        signals.triggers.short,
        'SHORT',
        'pink'
      );

      const longEntryTriggerConfluenceMarkers = createMarkers(
        signals.triggersConfluence.long,
        'LONG',
        'purple'
      );

      const shortEntryTriggerConfluenceMarkers = createMarkers(
        signals.triggersConfluence.short,
        'SHORT',
        'purple'
      );

      const strategyVisualization =
        formState.timeframes?.[timeframeIndex]?.strategy?.visualization;
      const shouldPlotEntryTriggers = Boolean(
        strategyVisualization?.plotEntryTriggers
      );
      const shouldPlotEntryConfluenceTriggers = Boolean(
        strategyVisualization?.plotEntryConfluenceTriggers
      );

      const longEntriesMarkers = createMarkers(
        (entries || [])
          .filter(({ side }) => side === 'LONG')
          .map((entry) => ({
            ...entry,
            time: snapToPreviousCandleTime(entry.time, candlesticks)
          }))
          .filter((entry) => entry.time != null),
        'LONG',
        'green'
      );

      const shortEntriesMarkers = createMarkers(
        (entries || [])
          .filter(({ side }) => side === 'SHORT')
          .map((entry) => ({
            ...entry,
            time: snapToPreviousCandleTime(entry.time, candlesticks)
          }))
          .filter((entry) => entry.time != null),
        'SHORT',
        'red'
      );

      const longAddsMarkers = createMarkers(
        (adds || [])
          .filter(({ side }) => side === 'LONG')
          .map((entry) => ({
            ...entry,
            time: snapToPreviousCandleTime(entry.time, candlesticks)
          }))
          .filter((entry) => entry.time != null),
        'LONG',
        '#00b8d9',
        { idPrefix: 'add-long' }
      );

      const shortAddsMarkers = createMarkers(
        (adds || [])
          .filter(({ side }) => side === 'SHORT')
          .map((entry) => ({
            ...entry,
            time: snapToPreviousCandleTime(entry.time, candlesticks)
          }))
          .filter((entry) => entry.time != null),
        'SHORT',
        '#00b8d9',
        { idPrefix: 'add-short' }
      );

      const allMarkers = [
        ...signalMarkers,
        ...(shouldPlotEntryTriggers
          ? [
              ...longEntryTriggerMarkers,
              ...shortEntryTriggersMarkers
            ]
          : []),
        ...(shouldPlotEntryConfluenceTriggers
          ? [
              ...longEntryTriggerConfluenceMarkers,
              ...shortEntryTriggerConfluenceMarkers
            ]
          : []),
        ...longEntriesMarkers,
        ...shortEntriesMarkers,
        ...longAddsMarkers,
        ...shortAddsMarkers
      ].sort((a, b) => (a.time as number) - (b.time as number));

      clearMarkers();
      markersPlugin.setMarkers(allMarkers);
    },
    [
      candlestickSeriesRef,
      chartInitialized,
      clearMarkers,
      ensureMarkersPlugin,
      entries,
      adds,
      signals,
      formState.timeframes,
      timeframeIndex,
    ]
  );

  useEffect(() => {
    if (!chartInitialized || !candlestickSeriesRef.current) {
      detachMarkers();
      return;
    }

    ensureMarkersPlugin();

    const subscription = candlestickData$.subscribe((candlesticks) => {
      renderMarkers(candlesticks);
    });

    return () => {
      subscription.unsubscribe();
      detachMarkers();
    };
  }, [
    candlestickData$,
    chartInitialized,
    candlestickSeriesRef,
    detachMarkers,
    ensureMarkersPlugin,
    renderMarkers
  ]);
};
